import React, { useState, useMemo, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import { Filter, Calendar, User, FileText, ChevronDown, Check, X, Search, Ban, Trash2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { getFieldValue } from './InsightsView';

// Helper to handle clicks outside popovers
export function useClickOutside<T extends HTMLElement>(onOutside: () => void) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    function h(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onOutside();
    }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onOutside]);
  return ref;
}

function ChipButton({ active, icon, label, onClick, onClear, children }: any) {
  return (
    <div className="relative inline-block">
      <div className={cn(
        "flex items-center text-xs border rounded-full transition-all bg-white cursor-pointer group",
        active ? "border-blue-500 ring-1 ring-blue-500 shadow-sm" : "border-gray-300 hover:border-gray-400"
      )}>
        <button onClick={onClick} className={cn("flex items-center gap-1.5 pl-2.5 py-1.5", onClear ? "pr-1" : "pr-2.5")}>
          <span className={cn(active ? "text-blue-500" : "text-gray-500")}>{icon}</span>
          <span className={cn("font-medium max-w-[150px] truncate", active ? "text-blue-900" : "text-gray-700")}>{label}</span>
          {!onClear && <ChevronDown size={12} className={cn("ml-0.5 opacity-50", active ? "text-blue-500" : "text-gray-400")} />}
        </button>
        {onClear && (
          <button onClick={(e) => { e.stopPropagation(); onClear(); }}
            className="px-1.5 py-1.5 text-gray-400 hover:text-red-500 transition-colors border-l border-transparent hover:border-red-100 rounded-r-full">
            <X size={12} />
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function Popover({ children, onClose, align = 'left', width = 280 }: any) {
  const ref = useClickOutside<HTMLDivElement>(onClose);
  return (
    <div
      ref={ref}
      className={cn(
        "absolute top-full mt-1.5 bg-white rounded-lg shadow-xl border border-gray-200 z-[2100] overflow-hidden",
        align === 'left' ? 'left-0' : 'right-0'
      )}
      style={{ width }}
    >
      {children}
    </div>
  );
}

export function PopoverHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="px-3 py-2.5 bg-gray-50 border-b border-gray-200">
      <div className="text-[11px] font-bold uppercase tracking-wide text-gray-700">{title}</div>
      {subtitle && <div className="text-[10px] text-gray-500 mt-0.5">{subtitle}</div>}
    </div>
  );
}

// Logic for zero count UX in select/radio options
function countResponsesForOption(allResponses: any[], fieldId: string, value: string, currentFilters: any, otherProps: any) {
    if (!allResponses) return 0;

    // Apply all filters EXCEPT the one for `fieldId`
    const { mode, selectedForms, selectedRespondent, startDate, endDate, allFields, filterText } = otherProps;

    const relevantResponses = allResponses.filter((r: any) => {
        if (mode === 'map' && !r.deviceInfo?.geolocation) return false;
        if (selectedForms && !selectedForms.includes(r.formId)) return false;
        if (selectedRespondent && selectedRespondent !== 'all' && r.respondentId !== selectedRespondent) return false;

        const rDate = new Date(r.startedAt);
        if (startDate && rDate < new Date(startDate)) return false;
        if (endDate) {
          const dEnd = new Date(endDate);
          dEnd.setHours(23, 59, 59, 999);
          if (rDate > dEnd) return false;
        }

        if (filterText) {
          const s = filterText.toLowerCase();
          const matchesMeta =
            r.id.toLowerCase().includes(s) ||
            r.respondentName.toLowerCase().includes(s);
          if (!matchesMeta) {
            let matchesData = false;
            for (const key in r.data) {
              if (String(r.data[key]?.value || '').toLowerCase().includes(s)) {
                matchesData = true; break;
              }
            }
            if (!matchesData) return false;
          }
        }

        for (const [fId, filterObj] of Object.entries(currentFilters || {})) {
          if (fId === fieldId) continue; // SKIP CURRENT FIELD

          if (!filterObj || Object.keys(filterObj as object).length === 0) continue;
          const ans = getFieldValue(r, fId, allFields);
          const fieldConfig = allFields?.find((f: any) => f.fieldId === fId);
          if (!fieldConfig) continue;

          if ((filterObj as any).notFilled === true) {
            if (ans !== undefined && ans !== null && ans !== '') return false;
            continue;
          } else if (ans === undefined || ans === null || ans === '') {
            if ((filterObj as any).value || (filterObj as any).min || (filterObj as any).max) return false;
            continue;
          }

          if (['select', 'radio', 'yes_no', 'text_short', 'text'].includes(fieldConfig.fieldType)) {
            if (Array.isArray((filterObj as any).value)) {
                if (!(filterObj as any).value.includes(String(ans))) return false;
            } else if ((filterObj as any).value && String(ans) !== (filterObj as any).value) return false;
          } else if (['date', 'datetime', 'time'].includes(fieldConfig.fieldType)) {
            let ansStr = String(ans);
            if (ansStr.includes('T')) ansStr = ansStr.split('T')[0];
            else if (ansStr.includes(' ')) ansStr = ansStr.split(' ')[0];
            ansStr = ansStr.substring(0, 10);
            
            if ((filterObj as any).min && ansStr < (filterObj as any).min) return false;
            if ((filterObj as any).max && ansStr > (filterObj as any).max) return false;
          } else if (['number', 'linear_scale', 'rating'].includes(fieldConfig.fieldType)) {
            const numAns = Number(ans);
            if (isNaN(numAns)) return false;
            if ((filterObj as any).min !== undefined && numAns < Number((filterObj as any).min)) return false;
            if ((filterObj as any).max !== undefined && numAns > Number((filterObj as any).max)) return false;
          }
        }
        return true;
    });

    return relevantResponses.filter((r: any) => {
        const ans = getFieldValue(r, fieldId, allFields);
        if (Array.isArray(value)) return value.includes(String(ans));
        return String(ans) === String(value);
    }).length;
}


function FieldFilterEditor({ field, value, onUpdate, onClose, onRemove, allResponses, currentFilters, otherProps }: any) {
  return (
    <Popover onClose={onClose} align="left" width={280}>
      <PopoverHeader title={field.fieldLabel} subtitle={`${field.formTitle} · ${field.fieldType}`} />
      <div className="p-3 space-y-2.5">
        <label className="flex items-center gap-2 text-[11px] text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={value?.notFilled || false}
            onChange={e => onUpdate('notFilled', e.target.checked)}
            className="w-3.5 h-3.5 text-red-500 border-gray-300 rounded focus:ring-red-400"
          />
          <Ban size={11} className="text-red-400" />
          Somente respostas em branco
        </label>

        {!value?.notFilled && (
          <>
            {['select', 'radio', 'yes_no', 'text_short', 'text'].includes(field.fieldType) && (
              <div>
                <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide mb-1 block">Valor(es)</label>
                <div className="max-h-40 overflow-y-auto space-y-1 custom-scrollbar">
                  <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer p-1.5 hover:bg-gray-50 rounded">
                    <input type="checkbox"
                      checked={!value?.value || (Array.isArray(value.value) && value.value.length === 0)}
                      onChange={() => onUpdate('value', [])}
                      className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-400" />
                    — Qualquer —
                  </label>
                  {field.fieldType === 'yes_no' ? (
                    <>
                      <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer p-1.5 hover:bg-gray-50 rounded">
                        <input type="checkbox" checked={Array.isArray(value?.value) ? value.value.includes('true') : value?.value === 'true'}
                          onChange={() => {
                            let curr = Array.isArray(value?.value) ? value.value : (value?.value ? [value.value] : []);
                            curr = curr.includes('true') ? curr.filter((x: string) => x !== 'true') : [...curr, 'true'];
                            onUpdate('value', curr);
                          }} className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-400" />
                        Sim
                      </label>
                      <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer p-1.5 hover:bg-gray-50 rounded">
                        <input type="checkbox" checked={Array.isArray(value?.value) ? value.value.includes('false') : value?.value === 'false'}
                          onChange={() => {
                            let curr = Array.isArray(value?.value) ? value.value : (value?.value ? [value.value] : []);
                            curr = curr.includes('false') ? curr.filter((x: string) => x !== 'false') : [...curr, 'false'];
                            onUpdate('value', curr);
                          }} className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-400" />
                        Não
                      </label>
                    </>
                  ) : (
                    (() => {
                      let finalOptions = field.options;
                      if (!finalOptions || finalOptions.length === 0) {
                        const uniqueValues = new Set<string>();
                        (allResponses || []).forEach((r: any) => {
                          const v = getFieldValue(r, field.fieldId, otherProps?.allFields);
                          if (v !== undefined && v !== null && v !== '') {
                             uniqueValues.add(String(v));
                          }
                        });
                        finalOptions = Array.from(uniqueValues).map(v => ({ value: v, label: v }));
                      }
                      
                      return finalOptions?.map((o: any, i: number) => {
                        const v = typeof o === 'object' ? o.value : o;
                        const l = typeof o === 'object' ? o.label : o;
                        const isChecked = Array.isArray(value?.value) ? value.value.includes(String(v)) : value?.value === String(v);
                        const countFilter = { ...(currentFilters || {}), [field.fieldId]: { value: [String(v)] } };
                        const count = countResponsesForOption(allResponses, field.fieldId, String(v), countFilter, otherProps);
                        const displayLabel = count === 0 ? `${l} (0)` : l;
                        const colorClass = count === 0 ? "text-gray-400" : "";
                        
                        return (
                          <label key={i} className={`flex items-center gap-2 text-xs cursor-pointer p-1.5 hover:bg-gray-50 rounded ${colorClass}`}>
                            <input type="checkbox" checked={isChecked}
                              onChange={() => {
                                let curr = Array.isArray(value?.value) ? value.value : (value?.value ? [value.value] : []);
                                curr = curr.includes(String(v)) ? curr.filter((x: string) => x !== String(v)) : [...curr, String(v)];
                                onUpdate('value', curr);
                              }}
                              className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-400" />
                            {displayLabel}
                          </label>
                        );
                      });
                    })()
                  )}
                </div>
              </div>
            )}

            {['date', 'datetime', 'time'].includes(field.fieldType) && (
              <div>
                <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide mb-1 block">Período</label>
                <div className="flex flex-col gap-2">
                  <input type="date" value={value?.min || ''} onChange={e => onUpdate('min', e.target.value)}
                    className="w-full text-xs p-1.5 bg-white border border-gray-300 rounded focus:ring-blue-400" />
                  <span className="text-gray-400 text-[10px] text-center font-semibold uppercase tracking-wider">Até</span>
                  <input type="date" value={value?.max || ''} onChange={e => onUpdate('max', e.target.value)}
                    className="w-full text-xs p-1.5 bg-white border border-gray-300 rounded focus:ring-blue-400" />
                </div>
              </div>
            )}

            {['number', 'linear_scale', 'rating'].includes(field.fieldType) && (
              <div>
                <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide mb-1 block">Intervalo</label>
                <div className="flex items-center gap-1.5">
                  <input type="number" placeholder="Mín" value={value?.min || ''} onChange={e => onUpdate('min', e.target.value)}
                    className="w-full text-xs p-1.5 bg-white border border-gray-300 rounded" />
                  <span className="text-gray-400 text-xs">—</span>
                  <input type="number" placeholder="Máx" value={value?.max || ''} onChange={e => onUpdate('max', e.target.value)}
                    className="w-full text-xs p-1.5 bg-white border border-gray-300 rounded" />
                </div>
              </div>
            )}

            {['text_long'].includes(field.fieldType) && (
              <div>
                <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide mb-1 block">Contém o texto</label>
                <input type="text" value={value?.value || ''} onChange={e => onUpdate('value', e.target.value)}
                  placeholder="Buscar…" className="w-full text-xs p-1.5 bg-white border border-gray-300 rounded" />
              </div>
            )}
          </>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <button onClick={onRemove} className="text-[11px] text-red-600 hover:text-red-700 font-semibold flex items-center gap-1">
            <Trash2 size={11} /> Remover
          </button>
          <button onClick={onClose} className="text-[11px] bg-gray-900 text-white hover:bg-gray-800 px-3 py-1.5 rounded font-semibold">
            Aplicar
          </button>
        </div>
      </div>
    </Popover>
  );
}

function buildDynamicFilterLabel(field: any, val: any): string {
  if (val.notFilled) return 'Em branco';
  if (val.value && (!Array.isArray(val.value) || val.value.length > 0)) {
    if (Array.isArray(val.value)) {
        if (val.value.length === 1) {
            const singleVal = val.value[0];
            if (field.fieldType === 'yes_no') return singleVal === 'true' ? 'Sim' : 'Não';
            const opt = field.options?.find((o: any) => (typeof o === 'object' ? String(o.value) : String(o)) === String(singleVal));
            return opt ? (typeof opt === 'object' ? opt.label : opt) : singleVal;
        }
        return `${val.value.length} selecionados`;
    } else {
        if (field.fieldType === 'yes_no') return val.value === 'true' ? 'Sim' : 'Não';
        const opt = field.options?.find((o: any) => (typeof o === 'object' ? String(o.value) : String(o)) === String(val.value));
        return opt ? (typeof opt === 'object' ? opt.label : opt) : val.value;
    }
  }
  if (val.min && val.max) {
      if (['date', 'datetime', 'time'].includes(field.fieldType)) {
          return `${format(new Date(val.min + 'T12:00:00'), 'dd/MM/yy')} a ${format(new Date(val.max + 'T12:00:00'), 'dd/MM/yy')}`;
      }
      return `${val.min} a ${val.max}`;
  }
  if (val.min) {
      if (['date', 'datetime', 'time'].includes(field.fieldType)) return `≥ ${format(new Date(val.min + 'T12:00:00'), 'dd/MM/yy')}`;
      return `≥ ${val.min}`;
  }
  if (val.max) {
      if (['date', 'datetime', 'time'].includes(field.fieldType)) return `≤ ${format(new Date(val.max + 'T12:00:00'), 'dd/MM/yy')}`;
      return `≤ ${val.max}`;
  }
  return 'Ativo';
}

function AddFilterMenu({ forms, selectedForms, allFields, dynamicFilters, updateDynamicFilter, accentColors }: any) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false));

  const availableFields = useMemo(() => {
    return allFields.filter((f: any) => {
      if (selectedForms.length > 0 && !selectedForms.includes(f.formId)) return false;
      if (dynamicFilters[f.fieldId]) return false;
      if (search && !f.fieldLabel.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    }).slice(0, 50); // limit to avoid lag
  }, [allFields, selectedForms, dynamicFilters, search]);

  const handleSelect = (f: any) => {
    if (['text_long'].includes(f.fieldType)) {
      updateDynamicFilter(f.fieldId, 'active', true);
    } else if (f.fieldType === 'yes_no') {
      updateDynamicFilter(f.fieldId, 'value', ['true']);
    } else if (['select', 'radio', 'text_short', 'text'].includes(f.fieldType)) {
      const firstVal = f.options?.[0] ? (typeof f.options[0] === 'object' ? f.options[0].value : f.options[0]) : '';
      if (firstVal) updateDynamicFilter(f.fieldId, 'value', [String(firstVal)]);
      else updateDynamicFilter(f.fieldId, 'active', true);
    } else if (['number', 'linear_scale', 'rating'].includes(f.fieldType)) {
      updateDynamicFilter(f.fieldId, 'min', '0');
    } else if (['date', 'datetime', 'time'].includes(f.fieldType)) {
      updateDynamicFilter(f.fieldId, 'active', true);
    } else {
      updateDynamicFilter(f.fieldId, 'active', true);
    }
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <button onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border border-dashed transition-colors",
          "border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 bg-gray-50 hover:bg-gray-100"
        )}>
        <Filter size={12} />
        Adicionar filtro
      </button>

      {open && (
        <div className="absolute top-full mt-1.5 left-0 bg-white rounded-lg shadow-xl border border-gray-200 z-[2100] w-72 overflow-hidden flex flex-col max-h-[400px]">
          <div className="p-2 border-b border-gray-100 shrink-0 relative">
            <Search size={12} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input autoFocus type="text" placeholder="Buscar pergunta..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-7 pr-2 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-gray-300"
            />
          </div>
          <div className="overflow-y-auto custom-scrollbar p-1 flex-1 min-h-0">
            {availableFields.length === 0 ? (
              <div className="p-3 text-center text-xs text-gray-400">Nenhum campo disponível.</div>
            ) : (
              availableFields.map((f: any) => (
                <button key={f.fieldId} onClick={() => handleSelect(f)}
                  className="w-full text-left px-2.5 py-1.5 hover:bg-gray-50 rounded flex flex-col gap-0.5 group">
                  <span className="text-xs font-medium text-gray-700 group-hover:text-gray-900 truncate block w-full">
                    {f.fieldLabel}
                  </span>
                  <span className="text-[10px] text-gray-400 truncate block w-full">
                    {f.formTitle} · {f.fieldType}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function FilterBar({
  filterText, setFilterText,
  forms, formColorMap, selectedForms, setSelectedForms,
  respondents, selectedRespondent, setSelectedRespondent,
  startDate, endDate, setStartDate, setEndDate,
  dynamicFilters, updateDynamicFilter, removeFieldFilter,
  allFields, hasActiveFilters, resetAllFilters, accentColors,
  filteredResponses, allResponses, mode
}: any) {
  const [open, setOpen] = useState<string | null>(null);
  const close = () => setOpen(null);

  const safeDate = (d: string) => new Date(d.includes('T') ? d : `${d}T12:00:00`);

  const periodLabel = (() => {
    if (!startDate && !endDate) return 'Qualquer data';
    if (startDate && endDate) return `${format(safeDate(startDate), 'dd/MM/yy')} → ${format(safeDate(endDate), 'dd/MM/yy')}`;
    if (startDate) return `Desde ${format(safeDate(startDate), 'dd/MM/yy')}`;
    return `Até ${format(safeDate(endDate), 'dd/MM/yy')}`;
  })();

  const respondentLabel = selectedRespondent === 'all'
    ? 'Todos os respondentes'
    : (respondents.find((r: any) => r[0] === selectedRespondent)?.[1] || 'Respondente');

  const formsLabel = selectedForms.length === forms.length
    ? `Todos os formulários (${forms.length})`
    : `${selectedForms.length} de ${forms.length} formulários`;

  return (
    <div className="bg-white border-b border-gray-200 px-5 py-2.5 flex items-center gap-2 flex-wrap shrink-0">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mr-1 flex items-center gap-1.5">
        <Filter size={12} /> Filtros
      </span>

      {/* Busca Geral */}
      {typeof filterText !== 'undefined' && setFilterText && (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
          <input
            type="text"
            placeholder="Busca Geral..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="pl-8 pr-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all w-48"
          />
          {filterText && (
            <button
              onClick={() => setFilterText('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={12} />
            </button>
          )}
        </div>
      )}


      {/* Formulários */}
      <ChipButton
        active={selectedForms.length !== forms.length}
        icon={<FileText size={12} />}
        label={formsLabel}
        onClick={() => setOpen(open === 'forms' ? null : 'forms')}
      >
        {open === 'forms' && (
          <Popover onClose={close} align="left" width={320}>
            <PopoverHeader title="Formulários" subtitle="Quais formulários incluir" />
            <div className="max-h-72 overflow-y-auto px-1 py-1 custom-scrollbar">
              <div className="flex items-center gap-2 px-2 py-1.5">
                <button
                  onClick={() => setSelectedForms(forms.map((f: any) => f.id))}
                  className="text-[11px] text-gray-600 hover:text-gray-900 font-medium"
                >Selecionar todos</button>
                <span className="text-gray-300">·</span>
                <button
                  onClick={() => setSelectedForms([])}
                  className="text-[11px] text-gray-600 hover:text-gray-900 font-medium"
                >Limpar</button>
              </div>
              {forms.map((f: any) => {
                const isSelected = selectedForms.includes(f.id);
                return (
                  <button key={f.id}
                    onClick={() => {
                      if (isSelected) setSelectedForms(selectedForms.filter((id: string) => id !== f.id));
                      else setSelectedForms([...selectedForms, f.id]);
                    }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded text-left transition-colors"
                  >
                    <div className={cn(
                      "w-4 h-4 rounded border flex items-center justify-center shrink-0",
                      isSelected ? "bg-blue-500 border-blue-500 text-white" : "border-gray-300 bg-white"
                    )}>
                      {isSelected && <Check size={10} strokeWidth={3} />}
                    </div>
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: formColorMap[f.id] || '#ccc' }} />
                    <span className="text-xs font-medium text-gray-700 truncate">{f.title}</span>
                  </button>
                );
              })}
            </div>
          </Popover>
        )}
      </ChipButton>

      {/* Período */}
      <ChipButton
        active={!!startDate || !!endDate}
        icon={<Calendar size={12} />}
        label={periodLabel}
        onClick={() => setOpen(open === 'period' ? null : 'period')}
        onClear={!!startDate || !!endDate ? () => { setStartDate(''); setEndDate(''); } : undefined}
      >
        {open === 'period' && (
          <Popover onClose={close} align="left" width={280}>
            <PopoverHeader title="Período" subtitle="Data de envio da resposta" />
            <div className="p-3 space-y-3">
              <div>
                <label className="text-[10px] font-semibold text-gray-500 uppercase block mb-1">Data Inicial</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                  className="w-full text-xs p-1.5 bg-gray-50 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-gray-500 uppercase block mb-1">Data Final</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                  className="w-full text-xs p-1.5 bg-gray-50 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
            </div>
          </Popover>
        )}
      </ChipButton>

      {/* Respondente */}
      <ChipButton
        active={selectedRespondent !== 'all'}
        icon={<User size={12} />}
        label={respondentLabel}
        onClick={() => setOpen(open === 'respondent' ? null : 'respondent')}
        onClear={selectedRespondent !== 'all' ? () => setSelectedRespondent('all') : undefined}
      >
        {open === 'respondent' && (
          <Popover onClose={close} align="left" width={260}>
            <PopoverHeader title="Respondente" subtitle="Quem preencheu o formulário" />
            <div className="max-h-64 overflow-y-auto p-1 custom-scrollbar">
              <button
                onClick={() => { setSelectedRespondent('all'); close(); }}
                className={cn("w-full text-left px-3 py-1.5 rounded text-xs flex items-center justify-between", selectedRespondent === 'all' ? "bg-blue-50 text-blue-700 font-bold" : "text-gray-700 hover:bg-gray-50")}
              >
                <span>Todos os respondentes</span>
                {selectedRespondent === 'all' && <Check size={12} className="text-blue-500 shrink-0 ml-2" />}
              </button>
              {respondents.map(([id, name]: [string, string]) => (
                <button key={id}
                  onClick={() => { setSelectedRespondent(id); close(); }}
                  className={cn("w-full text-left px-3 py-1.5 rounded text-xs flex items-center justify-between", selectedRespondent === id ? "bg-gray-100 font-bold text-gray-900" : "text-gray-700 hover:bg-gray-50")}
                >
                  <span className="truncate">{name}</span>
                  {selectedRespondent === id && <Check size={12} className="text-gray-700 shrink-0 ml-2" />}
                </button>
              ))}
            </div>
          </Popover>
        )}
      </ChipButton>

      {/* Field-specific filter chips */}
      {Object.entries(dynamicFilters).map(([fieldId, val]: [string, any]) => {
        const field = allFields.find((f: any) => f.fieldId === fieldId);
        if (!field) return null;
        const label = buildDynamicFilterLabel(field, val);
        const isOpen = open === `f-${fieldId}`;

        return (
          <ChipButton
            key={fieldId}
            active={true}
            icon={<Filter size={12} />}
            label={`${field.fieldLabel}: ${label}`}
            onClick={() => setOpen(isOpen ? null : `f-${fieldId}`)}
            onClear={() => removeFieldFilter(fieldId)}
          >
            {isOpen && (
              <FieldFilterEditor
                field={field}
                value={val}
                allResponses={allResponses}
                currentFilters={dynamicFilters}
                otherProps={{ mode, selectedForms, selectedRespondent, startDate, endDate, allFields, filterText }}
                onUpdate={(k: any, v: any) => updateDynamicFilter(fieldId, k, v)}
                onClose={close}
                onRemove={() => { removeFieldFilter(fieldId); close(); }}
              />
            )}
          </ChipButton>
        );
      })}

      <AddFilterMenu
        forms={forms}
        selectedForms={selectedForms}
        allFields={allFields}
        dynamicFilters={dynamicFilters}
        updateDynamicFilter={updateDynamicFilter}
        accentColors={accentColors}
      />

      {hasActiveFilters && (
        <button
          onClick={resetAllFilters}
          className="ml-auto text-[11px] font-semibold text-gray-500 hover:text-red-600 transition-colors flex items-center gap-1"
        >
          Limpar todos
        </button>
      )}
    </div>
  );
}
